import {
  type DataGridProps,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterModel,
  type GridPaginationModel,
  type GridSortModel,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { useQuery, type QueryKey } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiErrorMessage } from '@/shared/data/errors';
import { useDelayedFlag, useInvalidateQueries } from '@/shared/hooks';
import { defaultStaleTimeMs } from './gridDefaults';
import {
  computeStorageKey,
  getPersistedState,
  parseUrlState,
  type GridColumnWidthModel,
} from './gridState';
import { type GridQueryInput, type GridQueryResult, mapGridFilters } from './gridUtils';
import { useGridInitialState } from './useGridInitialState';
import { useGridPersistence } from './useGridPersistence';
import { useGridQueryInput } from './useGridQueryInput';
import { useGridQueryKey } from './useGridQueryKey';
import { useGridSortState } from './useGridSortState';
import { useGridUrlSync } from './useGridUrlSync';
import type { ApiError } from '@/shared/data/client';

type UseServerDataGridStateParams<T extends GridValidRowModel> = {
  queryPath: string;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  columns: Array<GridColDef<T>>;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  height?: number;
  noRowsText?: string;
  filteredNoRowsText?: string;
  onRowsChange?: (rows: T[]) => void;
  onQueryMeta?: (meta: { total: number; items: T[] } | null) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  fixedFilters?: GridQueryInput['filters'];
  search?: string;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryKeyPrefix?: QueryKey;
  queryKey?: QueryKey;
  onRefetch?: (refetch: () => Promise<void>) => void;
  onInvalidate?: (invalidate: () => Promise<void>) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  token?: string | null;
  organisationId?: string | null;
  projectId?: string | null;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  autoHeight?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  columnVisibilityPresets?: Record<string, GridColumnVisibilityModel>;
  columnVisibilityPreset?: string;
  refetchIntervalMs?: number | false;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | 'always';
  skeletonGetRowHeight?: DataGridProps['getRowHeight'] | null;
  getRowHeight?: DataGridProps['getRowHeight'];
};

type UseServerDataGridStateResult<T extends GridValidRowModel> = {
  errorMessage: string | null;
  rowsData: T[];
  rowCount: number;
  showInitialSkeleton: boolean;
  lastUpdatedAt: number | null;
  queryIsFetching: boolean;
  queryIsFetched: boolean;
  resolvedPageSizeOptions: number[];
  resolvedInitialPageSize: number;
  resolvedHeight: number | undefined;
  resolvedNoRowsText: string;
  resolvedSkeletonRowHeight: DataGridProps['getRowHeight'] | undefined;
  paginationModel: GridPaginationModel;
  setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
  handlePaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  handleSortModelChange: (model: GridSortModel) => void;
  captureScrollAnchor: () => void;
  filterModel: GridFilterModel;
  setFilterModel: Dispatch<SetStateAction<GridFilterModel>>;
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  columnWidthModel: GridColumnWidthModel;
  handleColumnWidthChange: NonNullable<DataGridProps['onColumnWidthChange']>;
};

const DEFAULT_VIEWPORT_HEIGHT = 780;

const useResolvedHeight = (autoHeight: boolean, height?: number): number | undefined => {
  const [viewportHeight, setViewportHeight] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_VIEWPORT_HEIGHT;
    return window.innerHeight;
  });

  useEffect(() => {
    if (autoHeight) return;
    if (typeof height === 'number') return;
    if (typeof window === 'undefined') return;
    const handleResize = (): void => {
      setViewportHeight(window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [autoHeight, height]);

  return useMemo(() => {
    if (autoHeight) return undefined;
    if (typeof height === 'number') return height;
    const buffer = 260;
    return Math.max(420, viewportHeight - buffer);
  }, [autoHeight, height, viewportHeight]);
};

const useGridStorageKey = ({
  persistKey,
  providedQueryKey,
  queryKeyPrefix,
  queryPath,
}: {
  persistKey?: string | null;
  providedQueryKey?: QueryKey;
  queryKeyPrefix?: QueryKey;
  queryPath: string;
}): string | null =>
  useMemo(
    () => computeStorageKey(persistKey, providedQueryKey, queryKeyPrefix, queryPath),
    [persistKey, providedQueryKey, queryKeyPrefix, queryPath],
  );

const useGridScrollRetention = ({
  setPaginationModel,
  setSortModelInternal,
  paginationPage,
  queryIsFetching,
  queryDataUpdatedAt,
}: {
  setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
  setSortModelInternal: (model: GridSortModel) => void;
  paginationPage: number;
  queryIsFetching: boolean;
  queryDataUpdatedAt: number;
}): {
  handlePaginationModelChange: (model: GridPaginationModel) => void;
  handleSortModelChange: (model: GridSortModel) => void;
  captureScrollAnchor: () => void;
} => {
  const pendingScrollRef = useRef<number | null>(null);
  const pendingPageRef = useRef<number | null>(null);
  const pendingScrollTypeRef = useRef<'pagination' | 'sort' | null>(null);
  const sortRestoreTimeoutRef = useRef<number | null>(null);
  const scrollLockRef = useRef<{ anchor: number; until: number } | null>(null);
  const scrollLockRafRef = useRef<number | null>(null);

  const maybeRestoreScroll = useCallback((target: number | null) => {
    if (target === null) return;
    const current = typeof window !== 'undefined' ? window.scrollY : null;
    if (current === null) return;
    if (Math.abs(current - target) <= 2) return;
    window.scrollTo({ top: target, behavior: 'auto' });
  }, []);

  const runScrollLock = useCallback(() => {
    if (!scrollLockRef.current) {
      if (scrollLockRafRef.current !== null) {
        window.cancelAnimationFrame(scrollLockRafRef.current);
        scrollLockRafRef.current = null;
      }
      return;
    }
    const { anchor, until } = scrollLockRef.current;
    if (Date.now() > until) {
      scrollLockRef.current = null;
      if (scrollLockRafRef.current !== null) {
        window.cancelAnimationFrame(scrollLockRafRef.current);
        scrollLockRafRef.current = null;
      }
      return;
    }
    maybeRestoreScroll(anchor);
    scrollLockRafRef.current = window.requestAnimationFrame(runScrollLock);
  }, [maybeRestoreScroll]);

  const startScrollLock = useCallback(
    (anchor: number) => {
      scrollLockRef.current = { anchor, until: Date.now() + 300 };
      scrollLockRafRef.current ??= window.requestAnimationFrame(runScrollLock);
    },
    [runScrollLock],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
      setPaginationModel(model);
      if (scrollY === null) return;
      pendingScrollRef.current = scrollY;
      pendingPageRef.current = model.page;
      pendingScrollTypeRef.current = 'pagination';
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          maybeRestoreScroll(scrollY);
        });
      }
    },
    [maybeRestoreScroll, setPaginationModel],
  );

  const captureScrollAnchor = useCallback(() => {
    const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
    if (scrollY === null) return;
    pendingScrollRef.current = scrollY;
    pendingPageRef.current = null;
    pendingScrollTypeRef.current = 'sort';
    startScrollLock(scrollY);
  }, [startScrollLock]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const scrollY = typeof window !== 'undefined' ? window.scrollY : null;
      setSortModelInternal(model);
      if (scrollY === null) return;
      pendingScrollRef.current = scrollY;
      pendingPageRef.current = null;
      pendingScrollTypeRef.current = 'sort';
      startScrollLock(scrollY);
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          maybeRestoreScroll(scrollY);
        });
      }
      if (sortRestoreTimeoutRef.current !== null) {
        window.clearTimeout(sortRestoreTimeoutRef.current);
      }
      sortRestoreTimeoutRef.current = window.setTimeout(() => {
        maybeRestoreScroll(scrollY);
        sortRestoreTimeoutRef.current = null;
      }, 120);
    },
    [maybeRestoreScroll, setSortModelInternal, startScrollLock],
  );

  useLayoutEffect(() => {
    if (pendingScrollRef.current === null) return;
    if (queryIsFetching) return;
    if (pendingPageRef.current !== null && pendingPageRef.current !== paginationPage) return;
    if (pendingScrollTypeRef.current === 'sort') return;
    const scrollY = pendingScrollRef.current;
    pendingScrollRef.current = null;
    pendingPageRef.current = null;
    pendingScrollTypeRef.current = null;
    window.setTimeout(() => {
      maybeRestoreScroll(scrollY);
    }, 0);
  }, [maybeRestoreScroll, paginationPage, queryIsFetching]);

  useLayoutEffect(() => {
    if (pendingScrollRef.current === null) return;
    if (queryIsFetching) return;
    if (pendingScrollTypeRef.current === 'sort') {
      const scrollY = pendingScrollRef.current;
      pendingScrollRef.current = null;
      pendingPageRef.current = null;
      pendingScrollTypeRef.current = null;
      maybeRestoreScroll(scrollY);
      scrollLockRef.current = null;
      return;
    }
    const scrollY = pendingScrollRef.current;
    pendingScrollRef.current = null;
    pendingPageRef.current = null;
    pendingScrollTypeRef.current = null;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(() => {
        maybeRestoreScroll(scrollY);
      });
    } else {
      window.setTimeout(() => {
        maybeRestoreScroll(scrollY);
      }, 0);
    }
  }, [maybeRestoreScroll, queryDataUpdatedAt, queryIsFetching]);

  useEffect(() => {
    return () => {
      if (sortRestoreTimeoutRef.current !== null) {
        window.clearTimeout(sortRestoreTimeoutRef.current);
      }
      if (scrollLockRafRef.current !== null) {
        window.cancelAnimationFrame(scrollLockRafRef.current);
      }
    };
  }, []);

  return {
    handlePaginationModelChange,
    handleSortModelChange,
    captureScrollAnchor,
  };
};

const useGridQueryLifecycle = <T extends GridValidRowModel>({
  finalQueryKey,
  token,
  queryFn,
  queryInput,
  staleTimeMs,
  refetchOnWindowFocus,
  refetchOnMount,
  refetchIntervalMs,
  fixedFiltersKey,
  search,
  paginationPage,
  setPaginationModel,
}: {
  finalQueryKey: QueryKey;
  token: string | null;
  queryFn: (input: GridQueryInput) => Promise<GridQueryResult<T>>;
  queryInput: GridQueryInput;
  staleTimeMs?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean | 'always';
  refetchIntervalMs?: number | false;
  fixedFiltersKey: string;
  search?: string;
  paginationPage: number;
  setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
}): {
  query: ReturnType<typeof useQuery<GridQueryResult<T>, ApiError>>;
  rowsData: T[];
  rowCount: number;
  showInitialSkeleton: boolean;
  lastUpdatedAt: number | null;
  queryIsFetching: boolean;
  queryIsFetched: boolean;
} => {
  const query = useQuery<GridQueryResult<T>, ApiError>({
    queryKey: finalQueryKey,
    enabled: Boolean(token),
    queryFn: async (): Promise<GridQueryResult<T>> => {
      return await queryFn(queryInput);
    },
    placeholderData: (previous) => previous,
    staleTime: staleTimeMs ?? defaultStaleTimeMs,
    refetchOnWindowFocus: refetchOnWindowFocus ?? false,
    refetchOnReconnect: true,
    refetchOnMount: refetchOnMount ?? false,
    refetchInterval: refetchIntervalMs,
  });

  const emptyRows = useMemo<T[]>(() => [], []);
  const rowsData: T[] = query.data?.items ?? emptyRows;
  const lastRowCountRef = useRef<number>(0);
  useEffect(() => {
    if (!query.data) return;
    lastRowCountRef.current = query.data.total;
  }, [query.data]);
  const rowCount = query.data?.total ?? (query.isFetching ? lastRowCountRef.current : 0);
  const showInitialSkeleton = useDelayedFlag(query.isFetching && !query.isFetched, 800);
  const lastUpdatedAt = query.dataUpdatedAt > 0 ? query.dataUpdatedAt : null;

  const lastFixedFiltersKey = useRef(fixedFiltersKey);
  useEffect(() => {
    if (lastFixedFiltersKey.current === fixedFiltersKey) return;
    lastFixedFiltersKey.current = fixedFiltersKey;
    if (paginationPage === 0) return;
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [fixedFiltersKey, paginationPage, setPaginationModel]);

  const searchKey = useMemo(() => search ?? '', [search]);
  const lastSearchKey = useRef(searchKey);
  useEffect(() => {
    if (lastSearchKey.current === searchKey) return;
    lastSearchKey.current = searchKey;
    if (paginationPage === 0) return;
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [paginationPage, searchKey, setPaginationModel]);

  return {
    query,
    rowsData,
    rowCount,
    showInitialSkeleton,
    lastUpdatedAt,
    queryIsFetching: query.isFetching,
    queryIsFetched: query.isFetched,
  };
};

const useGridCallbacks = <T extends GridValidRowModel>({
  onQueryInput,
  queryInput,
  queryInputKey,
  onRowsChange,
  rowsData,
  onQueryMeta,
  queryData,
  onDataUpdatedAt,
  lastUpdatedAt,
  onRefetch,
  onInvalidate,
  invalidateQueries,
  finalQueryKey,
  queryRefetch,
}: {
  onQueryInput?: (input: GridQueryInput) => void;
  queryInput: GridQueryInput;
  queryInputKey: string;
  onRowsChange?: (rows: T[]) => void;
  rowsData: T[];
  onQueryMeta?: (meta: { total: number; items: T[] } | null) => void;
  queryData: GridQueryResult<T> | undefined;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  lastUpdatedAt: number | null;
  onRefetch?: (refetch: () => Promise<void>) => void;
  onInvalidate?: (invalidate: () => Promise<void>) => void;
  invalidateQueries: ReturnType<typeof useInvalidateQueries>;
  finalQueryKey: QueryKey;
  queryRefetch: () => Promise<unknown>;
}): void => {
  const lastQueryInputKey = useRef<string | null>(null);
  useEffect(() => {
    if (!onQueryInput) return;
    if (lastQueryInputKey.current === queryInputKey) return;
    lastQueryInputKey.current = queryInputKey;
    onQueryInput(queryInput);
  }, [onQueryInput, queryInput, queryInputKey]);

  useEffect(() => {
    onRowsChange?.(rowsData);
  }, [rowsData, onRowsChange]);

  useEffect(() => {
    if (!onQueryMeta) return;
    if (!queryData) {
      onQueryMeta(null);
      return;
    }
    onQueryMeta({ total: queryData.total, items: queryData.items });
  }, [onQueryMeta, queryData]);

  useEffect(() => {
    if (!onDataUpdatedAt) return;
    onDataUpdatedAt(lastUpdatedAt);
  }, [lastUpdatedAt, onDataUpdatedAt]);

  useEffect(() => {
    if (onRefetch) {
      onRefetch(async () => {
        await queryRefetch();
      });
    }
  }, [onRefetch, queryRefetch]);

  useEffect(() => {
    if (onInvalidate) {
      onInvalidate(async () => {
        await invalidateQueries([{ queryKey: finalQueryKey }]);
      });
    }
  }, [finalQueryKey, invalidateQueries, onInvalidate]);
};

export const useServerDataGridState = <T extends GridValidRowModel>({
  queryPath,
  queryFn,
  columns,
  pageSizeOptions,
  initialPageSize,
  height,
  noRowsText = 'No results found',
  filteredNoRowsText,
  onRowsChange,
  onQueryMeta,
  onDataUpdatedAt,
  fixedFilters,
  search,
  initialSortModel: initialSortModelProp,
  initialFilterModel: initialFilterModelProp,
  queryKeyPrefix,
  queryKey: providedQueryKey,
  onRefetch,
  onInvalidate,
  onQueryInput,
  token: tokenProp,
  organisationId,
  projectId,
  persistKey,
  syncStateToUrl = false,
  autoHeight = false,
  columnVisibilityModel: columnVisibilityModelProp,
  columnVisibilityPresets,
  columnVisibilityPreset,
  refetchIntervalMs,
  staleTimeMs,
  refetchOnWindowFocus,
  refetchOnMount,
  skeletonGetRowHeight,
  getRowHeight,
}: UseServerDataGridStateParams<T>): UseServerDataGridStateResult<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const invalidateQueries = useInvalidateQueries();
  const urlState = useMemo(
    () => parseUrlState(searchParams, syncStateToUrl),
    [searchParams, syncStateToUrl],
  );

  const baseStorageKey = useGridStorageKey({
    persistKey,
    providedQueryKey,
    queryKeyPrefix,
    queryPath,
  });

  const resolvedHeight = useResolvedHeight(autoHeight, height);

  const persistedState = useMemo(() => getPersistedState(baseStorageKey), [baseStorageKey]);
  const {
    resolvedPageSizeOptions,
    resolvedInitialPageSize,
    initialPaginationModel,
    initialSortModel,
    initialFilterModel,
    initialColumnVisibilityModel,
    initialColumnWidthModel,
  } = useGridInitialState({
    columns,
    pageSizeOptions,
    initialPageSize,
    urlState,
    persistedState,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
  });

  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(initialPaginationModel);
  const { sortModel, handleSortModelChange: setSortModelInternal } =
    useGridSortState(initialSortModel);
  const [filterModel, setFilterModel] = useState<GridFilterModel>(initialFilterModel);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    initialColumnVisibilityModel,
  );
  const [columnWidthModel, setColumnWidthModel] =
    useState<GridColumnWidthModel>(initialColumnWidthModel);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const toErrorMessage = useApiErrorMessage();
  const token = tokenProp ?? null;

  useGridPersistence({
    baseStorageKey,
    organisationId,
    projectId,
    paginationModel,
    sortModel,
    filterModel,
    columnVisibilityModel,
    columnWidthModel,
  });

  const handleColumnWidthChange = useCallback<NonNullable<DataGridProps['onColumnWidthChange']>>(
    (params) => {
      const width = params.width;
      const field = params.colDef.field;
      if (typeof width !== 'number') return;
      setColumnWidthModel((prev) => {
        if (prev[field] === width) return prev;
        return { ...prev, [field]: width };
      });
    },
    [],
  );

  useEffect(() => {
    if (!resolvedPageSizeOptions.includes(paginationModel.pageSize)) {
      setPaginationModel((prev) => ({ ...prev, page: 0, pageSize: resolvedInitialPageSize }));
    }
  }, [paginationModel.pageSize, resolvedInitialPageSize, resolvedPageSizeOptions]);

  const { queryInput, queryInputKey, fixedFiltersKey } = useGridQueryInput({
    paginationModel,
    sortModel,
    filterModel,
    fixedFilters,
    search,
  });
  const finalQueryKey = useGridQueryKey({
    queryPath,
    queryKeyPrefix,
    providedQueryKey,
    organisationId,
    projectId,
    queryInputKey,
  });

  const {
    query,
    rowsData,
    rowCount,
    showInitialSkeleton,
    lastUpdatedAt,
    queryIsFetching,
    queryIsFetched,
  } = useGridQueryLifecycle<T>({
    finalQueryKey,
    token,
    queryFn,
    queryInput,
    staleTimeMs,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchIntervalMs,
    fixedFiltersKey,
    search,
    paginationPage: paginationModel.page,
    setPaginationModel,
  });

  const { handlePaginationModelChange, handleSortModelChange, captureScrollAnchor } =
    useGridScrollRetention({
      setPaginationModel,
      setSortModelInternal,
      paginationPage: paginationModel.page,
      queryIsFetching,
      queryDataUpdatedAt: query.dataUpdatedAt,
    });

  useGridCallbacks<T>({
    onQueryInput,
    queryInput,
    queryInputKey,
    onRowsChange,
    rowsData,
    onQueryMeta,
    queryData: query.data,
    onDataUpdatedAt,
    lastUpdatedAt,
    onRefetch,
    onInvalidate,
    invalidateQueries,
    finalQueryKey,
    queryRefetch: query.refetch,
  });

  useEffect(() => {
    if (query.status === 'error') {
      setErrorMessage(toErrorMessage(query.error));
    }
    if (query.status === 'success') {
      setErrorMessage(null);
    }
  }, [query.error, query.status, toErrorMessage]);

  useGridUrlSync({
    syncStateToUrl,
    searchParams,
    setSearchParams,
    paginationModel,
    sortModel,
    filterModel,
  });

  const hasActiveFilters = mapGridFilters(filterModel).length > 0;
  const resolvedNoRowsText = hasActiveFilters
    ? (filteredNoRowsText ?? 'No results match your filters')
    : noRowsText;
  const resolvedSkeletonRowHeight =
    skeletonGetRowHeight === null ? undefined : (skeletonGetRowHeight ?? getRowHeight);

  return {
    errorMessage,
    rowsData,
    rowCount,
    showInitialSkeleton,
    lastUpdatedAt,
    queryIsFetching,
    queryIsFetched,
    resolvedPageSizeOptions,
    resolvedInitialPageSize,
    resolvedHeight,
    resolvedNoRowsText,
    resolvedSkeletonRowHeight,
    paginationModel,
    setPaginationModel,
    handlePaginationModelChange,
    sortModel,
    handleSortModelChange,
    captureScrollAnchor,
    filterModel,
    setFilterModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
    columnWidthModel,
    handleColumnWidthChange,
  };
};
